// src/features/crypto-info/components/CryptoPriceVolumeChart.tsx
//
// Component Island: interactive candlestick Price / Volume chart with
// technical-indicator overlays for the Crypto Info deep-dive page. Mirrors the
// stock-info IntradayChart but renders true OHLC candlesticks (green = up,
// red = down) for clearer price-action reading. Users can:
//   - toggle the technical indicator (SMA / EMA / WMA / RSI / ADX),
//   - pick the interval / timeframe (15min .. 1day),
//   - set the indicator period length, and
//   - choose a From / To calendar window,
// mirroring FMP's /stable/technical-indicators/{indicator} endpoint (proxied by
// GET /api/v1/crypto-info/{symbol}/technical-indicators).
//
// SMA/EMA/WMA are price-scale overlays drawn on the main chart. RSI/ADX are
// bounded 0-100 oscillators, so they render in a dedicated sub-panel with a
// 0-100 axis (RSI adds 30/70 reference bands, ADX a 25 trend-strength line).
//
// Recharts has no native candlestick, so each bar encodes its full price range
// as a [low, high] array dataKey and a custom <Candle/> shape draws the wick
// (high→low) plus the open/close body. FMP returns bars newest-first; Recharts
// plots left-to-right chronologically, so we reverse() a shallow copy first.
// FMP crypto timestamps arrive as "YYYY-MM-DD HH:MM:SS" (space-separated), so
// we slice the string for ticks rather than constructing a Date.

import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    fetchCryptoTechnicalIndicator,
    type TechnicalIndicator,
} from '../api';

const INDICATORS: { label: string; value: TechnicalIndicator }[] = [
    { label: 'SMA', value: 'sma' },
    { label: 'EMA', value: 'ema' },
    { label: 'WMA', value: 'wma' },
    { label: 'RSI', value: 'rsi' },
    { label: 'ADX', value: 'adx' },
];

const TIMEFRAMES = [
    { label: '15 Min', value: '15min' },
    { label: '30 Min', value: '30min' },
    { label: '1 Hour', value: '1hour' },
    { label: '4 Hour', value: '4hour' },
    { label: '1 Day', value: '1day' },
] as const;

// RSI/ADX are 0-100 oscillators; the rest overlay directly on the price scale.
const OSCILLATORS: TechnicalIndicator[] = ['rsi', 'adx'];

const INDICATOR_COLOR = '#f59e0b'; // amber overlay line
const UP_COLOR = '#10b981'; // emerald (close >= open)
const DOWN_COLOR = '#ef4444'; // red (close < open)

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const isoDaysAgo = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

const formatVolume = (v: number): string => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v}`;
};

// Sub-dollar coins (e.g. DOGE ~$0.07) need extra precision.
const formatUsd = (v: number): string =>
    `$${v.toLocaleString(undefined, { maximumFractionDigits: v < 1 ? 6 : 2 })}`;

const formatAxisPrice = (v: number): string =>
    v < 1 ? `$${v.toPrecision(3)}` : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Chart-internal row: the API bar plus the [low, high] tuple the candlestick
// shape encodes as its dataKey. The index signature widens to allow the tuple
// alongside the raw OHLCV/indicator scalars.
interface CandleBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    range: [number, number];
    [key: string]: number | string | [number, number];
}

// A single OHLC candlestick. Recharts hands us the pixel box for the encoded
// [low, high] range (y = pixel of `high`, height = span down to `low`), so we
// linearly map the intermediate open/close prices to pixels off that range.
interface CandleShapeProps {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    payload?: CandleBar;
}

const Candle = (props: CandleShapeProps) => {
    const { x, y, width, height, payload } = props;
    if (
        x == null ||
        y == null ||
        width == null ||
        height == null ||
        !payload
    ) {
        return null;
    }

    const open = Number(payload.open);
    const close = Number(payload.close);
    const high = Number(payload.high);
    const low = Number(payload.low);
    if (
        !Number.isFinite(open) ||
        !Number.isFinite(close) ||
        !Number.isFinite(high) ||
        !Number.isFinite(low)
    ) {
        return null;
    }

    const isUp = close >= open;
    const color = isUp ? UP_COLOR : DOWN_COLOR;
    const range = high - low;
    // pixels-per-price-unit within this candle's [low, high] box.
    const ratio = range > 0 ? height / range : 0;

    const centerX = x + width / 2;
    const bodyTopPrice = Math.max(open, close);
    const bodyBottomPrice = Math.min(open, close);
    const bodyY = ratio ? y + (high - bodyTopPrice) * ratio : y;
    const bodyHeight = ratio
        ? Math.max((bodyTopPrice - bodyBottomPrice) * ratio, 1)
        : 1;
    // Keep a hair of horizontal padding so adjacent candles don't merge.
    const bodyWidth = Math.max(width * 0.7, 1);
    const bodyX = centerX - bodyWidth / 2;

    return (
        <g stroke={color} fill={color}>
            {/* Wick: high -> low */}
            <line
                x1={centerX}
                y1={y}
                x2={centerX}
                y2={y + height}
                strokeWidth={1}
            />
            {/* Body: open -> close */}
            <rect
                x={bodyX}
                y={bodyY}
                width={bodyWidth}
                height={bodyHeight}
                fillOpacity={0.9}
            />
        </g>
    );
};

// Bespoke tooltip so the [low, high] range dataKey renders as clean OHLC lines.
interface CandleTooltipProps {
    active?: boolean;
    payload?: { payload: CandleBar }[];
    label?: string;
    indicator: TechnicalIndicator;
}

const CandleTooltip = ({
    active,
    payload,
    label,
    indicator,
}: CandleTooltipProps) => {
    if (!active || !payload || !payload.length) return null;
    const bar = payload[0].payload;
    const indicatorVal = bar[indicator];
    return (
        <div className="rounded-lg border border-slate-700 bg-slate-900/95 px-3 py-2 text-xs text-slate-200 shadow-lg">
            <div className="mb-1 font-semibold text-slate-400">{label}</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                <span className="text-slate-400">Open</span>
                <span className="text-right font-mono">
                    {formatUsd(Number(bar.open))}
                </span>
                <span className="text-slate-400">High</span>
                <span className="text-right font-mono">
                    {formatUsd(Number(bar.high))}
                </span>
                <span className="text-slate-400">Low</span>
                <span className="text-right font-mono">
                    {formatUsd(Number(bar.low))}
                </span>
                <span className="text-slate-400">Close</span>
                <span className="text-right font-mono">
                    {formatUsd(Number(bar.close))}
                </span>
                <span className="text-slate-400">Volume</span>
                <span className="text-right font-mono">
                    {formatVolume(Number(bar.volume))}
                </span>
                {typeof indicatorVal === 'number' && (
                    <>
                        <span className="text-amber-400">
                            {indicator.toUpperCase()}
                        </span>
                        <span className="text-right font-mono text-amber-400">
                            {OSCILLATORS.includes(indicator)
                                ? indicatorVal.toFixed(2)
                                : formatUsd(indicatorVal)}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export const CryptoPriceVolumeChart = ({ symbol }: { symbol: string }) => {
    const [indicator, setIndicator] = useState<TechnicalIndicator>('sma');
    const [timeframe, setTimeframe] = useState<string>('1day');
    const [periodLength, setPeriodLength] = useState<number>(20);
    const [fromDate, setFromDate] = useState<string>(isoDaysAgo(180));
    const [toDate, setToDate] = useState<string>(todayIso());

    const [data, setData] = useState<CandleBar[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isOscillator = OSCILLATORS.includes(indicator);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchCryptoTechnicalIndicator(symbol, {
            indicator,
            periodLength,
            timeframe,
            from: fromDate || undefined,
            to: toDate || undefined,
        })
            .then((res) => {
                // Reverse so Recharts plots oldest -> newest (left to right),
                // and attach the [low, high] range the candlestick shape needs.
                if (!cancelled) {
                    const chrono: CandleBar[] = [...res].reverse().map(
                        (bar) => ({
                            ...bar,
                            range: [Number(bar.low), Number(bar.high)] as [
                                number,
                                number,
                            ],
                        }),
                    );
                    setData(chrono);
                }
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Chart unavailable.');
                    setData([]);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol, indicator, timeframe, periodLength, fromDate, toDate]);

    // Pad the price Y-domain around the full high/low envelope so candles fit.
    const [yMin, yMax] = useMemo<[number, number | 'auto']>(() => {
        if (!data.length) return [0, 'auto'];
        const highs = data
            .map((d) => Number(d.high))
            .filter((n) => Number.isFinite(n));
        const lows = data
            .map((d) => Number(d.low))
            .filter((n) => Number.isFinite(n));
        if (!highs.length || !lows.length) return [0, 'auto'];
        return [Math.min(...lows) * 0.98, Math.max(...highs) * 1.02];
    }, [data]);

    // Intraday bars show HH:MM; daily bars show the calendar date.
    const formatXTick = (str: string) =>
        timeframe === '1day' ? str.substring(0, 10) : str.substring(5, 16);

    const toggleClass = (active: boolean) =>
        `px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            active
                ? 'bg-orange-500 text-white shadow'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
        }`;

    const inputClass =
        'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-slate-700 dark:text-slate-200';

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm flex flex-col">
            {/* Header + indicator toggles */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Price &amp; Volume
                </h2>

                <div className="flex flex-wrap bg-slate-100 dark:bg-slate-900/50 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                    {INDICATORS.map((ind) => (
                        <button
                            key={ind.value}
                            onClick={() => setIndicator(ind.value)}
                            className={toggleClass(indicator === ind.value)}
                        >
                            {ind.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Interval + period + date-window controls */}
            <div className="flex flex-wrap items-end gap-3 mb-6 text-xs">
                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        Interval
                    </label>
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className={inputClass}
                    >
                        {TIMEFRAMES.map((tf) => (
                            <option key={tf.value} value={tf.value}>
                                {tf.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        Period
                    </label>
                    <input
                        type="number"
                        min={2}
                        max={200}
                        value={periodLength}
                        onChange={(e) =>
                            setPeriodLength(
                                Math.max(
                                    2,
                                    Math.min(200, Number(e.target.value) || 2),
                                ),
                            )
                        }
                        className={`w-20 ${inputClass}`}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        From
                    </label>
                    <input
                        type="date"
                        value={fromDate}
                        max={toDate || todayIso()}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-slate-500 dark:text-slate-400 font-medium">
                        To
                    </label>
                    <input
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        max={todayIso()}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Main candlestick + volume chart */}
            <div className="relative h-[340px] w-full">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-orange-500 font-medium animate-pulse">
                            Loading Chart…
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error}
                    </div>
                )}

                {!isLoading && !error && data.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No data available for this window.
                    </div>
                )}

                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{ left: -4, bottom: 0, top: 10, right: -4 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.3}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatXTick}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            minTickGap={40}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            yAxisId="price"
                            orientation="right"
                            domain={[yMin, yMax]}
                            tickFormatter={formatAxisPrice}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={70}
                        />
                        <YAxis
                            yAxisId="volume"
                            orientation="left"
                            domain={[0, (dataMax: number) => dataMax * 4]}
                            tickFormatter={formatVolume}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={46}
                        />
                        <Tooltip
                            content={<CandleTooltip indicator={indicator} />}
                        />
                        <Bar
                            yAxisId="volume"
                            dataKey="volume"
                            name="Volume"
                            fill="#64748b"
                            opacity={0.25}
                            isAnimationActive={false}
                        />
                        {/* Candlesticks encode [low, high]; <Candle/> draws OHLC. */}
                        <Bar
                            yAxisId="price"
                            dataKey="range"
                            name="Price"
                            shape={<Candle />}
                            isAnimationActive={false}
                        />
                        {!isOscillator && (
                            <Line
                                yAxisId="price"
                                type="monotone"
                                dataKey={indicator}
                                name={indicator.toUpperCase()}
                                stroke={INDICATOR_COLOR}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                                connectNulls
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Oscillator sub-panel (RSI / ADX) */}
            {isOscillator && (
                <div className="relative h-[150px] w-full mt-4">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        {indicator.toUpperCase()} ({periodLength})
                    </div>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={data}
                            margin={{ left: -4, bottom: 0, top: 6, right: -4 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatXTick}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                minTickGap={40}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                orientation="right"
                                domain={[0, 100]}
                                ticks={[0, 30, 50, 70, 100]}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={70}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    borderColor: '#334155',
                                    borderRadius: '8px',
                                }}
                                labelStyle={{
                                    color: '#94a3b8',
                                    marginBottom: '4px',
                                }}
                                formatter={(value: number) => [
                                    Number(value).toFixed(2),
                                    indicator.toUpperCase(),
                                ]}
                            />
                            {indicator === 'rsi' && (
                                <>
                                    <ReferenceLine
                                        y={70}
                                        stroke="#ef4444"
                                        strokeDasharray="4 4"
                                    />
                                    <ReferenceLine
                                        y={30}
                                        stroke="#22c55e"
                                        strokeDasharray="4 4"
                                    />
                                </>
                            )}
                            {indicator === 'adx' && (
                                <ReferenceLine
                                    y={25}
                                    stroke="#94a3b8"
                                    strokeDasharray="4 4"
                                />
                            )}
                            <Line
                                type="monotone"
                                dataKey={indicator}
                                name={indicator.toUpperCase()}
                                stroke={INDICATOR_COLOR}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                                connectNulls
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default CryptoPriceVolumeChart;
